import {useEffect} from '@wordpress/element';
import {Elements, ExpressCheckoutElement} from '@stripe/react-stripe-js';
import {sprintf} from '@wordpress/i18n';
import {
    initStripe as loadStripe,
    isFieldRequired,
    mapLineItems,
    getErrorMessage,
    formatPaymentMethod,
    ucfirst
} from '@wc-stripe/blocks-utils';
import {TokenizedPaymentMethod} from '@wc-stripe/blocks-components';
import {
    useCreatePaymentMethod,
    useExpressCheckout,
    useProcessCheckoutSuccess,
    useProcessExpressCheckoutError,
    useElementOptions,
    useValidatePaymentExists
} from '../hooks';

/**
 * All payment methods default to 'never'. Each gateway's activeMethod
 * overrides exactly one entry to its desired display rule.
 */
const defaultPaymentMethods = {
    applePay: 'never',
    googlePay: 'never',
    amazonPay: 'never',
    paypal: 'never',
    klarna: 'never',
    link: 'never'
};

/**
 * Inner component — renders the ExpressCheckoutElement and wires up all hooks.
 * Button options (height, type, theme) are read from data.button.* directly.
 * The paymentMethodKey (e.g. 'applePay') is derived from activeMethod.
 */
const ExpressCheckoutComponent = ({data, name, activeMethod, isExpress = true, ...wcProps}) => {
    const {
        onClick = null,
        onClose = null,
        onError = () => {
        },
        billing,
        onSubmit,
        cartData,
        emitResponse,
        shippingData,
        eventRegistration,
        activePaymentMethod
    } = wcProps;

    const {cartTotal, billingAddress} = billing;
    const {onCheckoutFail, onCheckoutSuccess, onCheckoutValidation} = eventRegistration;
    const {needsShipping, shippingAddress} = shippingData;
    const {extensions} = cartData;
    const {lineItems} = extensions?.wc_stripe?.cart || {};

    const gatewayId = data.gatewayId;
    const elementType = 'expressCheckout';
    const total = parseInt(cartTotal.value);

    // Derive the payment method key from activeMethod so buttonType/buttonTheme
    // are keyed correctly (e.g. {applePay: 'buy'}) without needing separate props.
    const paymentMethodKey = Object.keys(activeMethod)[0];
    const buttonType = data.button?.type ? {[paymentMethodKey]: data.button.type} : null;
    const buttonTheme = data.button?.theme ? {[paymentMethodKey]: data.button.theme} : null;

    const options = {
        buttonHeight: Math.max(40, Math.min(55, parseInt(data.button.height))),
        ...(buttonType && {buttonType}),
        ...(buttonTheme && {buttonTheme}),
        paymentMethods: {...defaultPaymentMethods, ...activeMethod},
        emailRequired: isExpress && isFieldRequired('email'),
        phoneNumberRequired: isExpress && isFieldRequired('phone'),
        billingAddressRequired: isExpress,
        shippingAddressRequired: isExpress && needsShipping
    };

    const {
        events,
        paymentMethodData,
        createPaymentMethod,
        clearPaymentMethodData
    } = useCreatePaymentMethod({
        name,
        gatewayId,
        emitResponse,
        billingAddress,
        shippingAddress,
        eventRegistration,
        shouldSavePayment: false,
        activePaymentMethod
    });

    useValidatePaymentExists({
        name,
        isExpress,
        paymentMethod: paymentMethodData,
        errorMessage: sprintf(getErrorMessage({code: 'order_button_click'}), data.title),
        emitResponse,
        activePaymentMethod,
        onCheckoutValidation
    });

    const {
        onConfirm,
        onButtonClick,
        onShippingChange
    } = useExpressCheckout({
        name,
        total,
        events,
        onClick,
        lineItems: mapLineItems(lineItems),
        isExpress,
        shippingData,
        paymentMethod: gatewayId,
        activePaymentMethod,
        createPaymentMethod
    });

    useEffect(() => {
        if (isExpress && paymentMethodData?.id) {
            onSubmit();
        }
    }, [paymentMethodData]);

    useProcessCheckoutSuccess({
        name,
        elementType,
        emitResponse,
        billingAddress,
        onCheckoutSuccess,
        activePaymentMethod
    });

    useProcessExpressCheckoutError({
        name,
        onError,
        emitResponse,
        onCheckoutFail,
        activePaymentMethod,
        clearPaymentMethodData
    });

    if (!isExpress && paymentMethodData) {
        const isLink = paymentMethodData.type === 'link';
        const alt = isLink ? 'Link' : paymentMethodData.card.display_brand;
        const brand = isLink ? 'link' : paymentMethodData.card.brand;
        const description = isLink
            ? paymentMethodData.link.email
            : formatPaymentMethod(data.paymentMethodFormat, {
                brand: ucfirst(paymentMethodData.card.brand),
                last4: paymentMethodData.card.last4,
                masked_number: `********${paymentMethodData.card.last4}`
            });
        return (
            <TokenizedPaymentMethod
                alt={alt}
                brand={brand}
                description={description}
                actions={[
                    {
                        type: 'cancel',
                        label: 'Cancel',
                        onClick: () => {
                            clearPaymentMethodData();
                        }
                    }
                ]}/>
        )
    }

    return (
        <ExpressCheckoutElement
            options={options}
            onClick={(event) => onButtonClick(event)}
            onCancel={() => onClose?.()}
            onConfirm={(event) => onConfirm(event)}
            onLoadError={error => console.log(error)}
            onShippingAddressChange={onShippingChange}
            onShippingRateChange={onShippingChange}
        />
    );
};

/**
 * Shared content component for express checkout gateways.
 * Border radius is read from data.button.radius.
 *
 * @param {Object}  props
 * @param {Object}  props.data             - Gateway settings object from getSetting()
 * @param {string}  props.name             - Express payment method name
 * @param {Object}  props.activeMethod     - e.g. {applePay: 'auto'} or {link: 'auto'}
 */
export const ExpressCheckoutContent = ({data, name, activeMethod, ...wcProps}) => {
    const {billing} = wcProps;
    const {cartTotal, currency} = billing;

    const options = useElementOptions({
        gatewayId: data.gatewayId,
        cartTotal,
        currency,
        expressCheckout: true,
        elementOptions: {
            paymentMethodCreation: 'manual',
            appearance: {variables: {borderRadius: data.button.radius}},
            ...data.elementOptions
        }
    });


    return (
        <Elements stripe={loadStripe} options={options}>
            <ExpressCheckoutComponent
                data={data}
                name={name}
                activeMethod={activeMethod}
                {...wcProps}
            />
        </Elements>
    )
};

